import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
} from "@chakra-ui/react";
import { FunctionComponent, useState } from "react";
import { MODAL_CONTENT_SIZING, MODAL_SIZING } from "../utils/constants";
import { useForm } from "react-hook-form";
import { useAppState } from "../state/useAppState";
import { validateApiKeyAsync } from "../api/api";

interface EntityModalProps {
    show: boolean;
    onClose: () => void;
    allowDismissWithoutApiKey?: boolean;
}

type FormFields = {
    apiKey: string;
};

export const ApiKeyPopup: FunctionComponent<EntityModalProps> = ({ show, onClose, allowDismissWithoutApiKey = false }) => {
    const apiKey = useAppState(state => state.apiKey);
    const setApiKeyAndApplication = useAppState(state => state.setApiKeyAndApplication);
    const [isSavingApiKey, setIsSavingApiKey] = useState(false);

    const formValues: FormFields = {
        apiKey: "",
    };

    const {
        handleSubmit,
        register,
        formState: { errors },
        setError,
    } = useForm<FormFields>({ values: formValues });

    const onSubmit = async (data: FormFields) => {
        setIsSavingApiKey(true);

        try {
            // Validate API key and get application information
            const applicationInfo = await validateApiKeyAsync(data.apiKey);

            // Store both API key and application info
            setApiKeyAndApplication(data.apiKey, applicationInfo);

            setIsSavingApiKey(false);
            onClose();
        } catch (error) {
            setIsSavingApiKey(false);
            const errorMessage = error instanceof Error ? error.message : "Invalid API key";
            setError("apiKey", { type: "manual", message: errorMessage });
        }
    };

    // Prevent closing if there's no API key stored (required on first use)
    const handleClose = () => {
        if (apiKey || allowDismissWithoutApiKey) {
            onClose();
        }
    };

    return (
        <Modal
            isOpen={show}
            onClose={handleClose}
            size={MODAL_SIZING}
            isCentered
            motionPreset="slideInBottom"
            closeOnOverlayClick={!!apiKey || allowDismissWithoutApiKey}
            closeOnEsc={!!apiKey || allowDismissWithoutApiKey}
        >
            <ModalOverlay />
            <ModalContent style={{ ...MODAL_CONTENT_SIZING }} overflowY="auto">
                {(apiKey || allowDismissWithoutApiKey) && <ModalCloseButton />}
                <ModalHeader>{apiKey ? "Update API key" : "API Key Required"}</ModalHeader>
                <ModalBody>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Flex gap="2rem" mb="2rem" flexFlow="column nowrap">
                            <Flex flexDirection="column" gap={4}>
                                <FormControl isRequired isInvalid={!!errors.apiKey}>
                                    <Input
                                        {...register("apiKey", { required: "API key is required" })}
                                        type="password"
                                        placeholder="Enter your API key"
                                        autoFocus
                                    />
                                    <FormErrorMessage>{errors.apiKey?.message}</FormErrorMessage>
                                </FormControl>
                            </Flex>
                        </Flex>
                        <Button type="submit" variant="primary" mb="2rem" isLoading={isSavingApiKey} isDisabled={isSavingApiKey}>
                            {apiKey ? "Update" : "Add"}
                        </Button>
                    </form>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
